import React from "react";
import { Sparkles, Car, Wrench, ArrowUpRight, Check } from "lucide-react";

const VEHICLE_SIZES = [
  { key: "coupe", label: "2 Door Coupe" },
  { key: "sedan", label: "4 Door Sedan" },
  { key: "small_suv", label: "Small SUV" },
  { key: "large_suv", label: "Large SUV" },
  { key: "truck", label: "Full Size Truck" },
];

const SERVICES = [
  {
    id: "exterior",
    icon: Sparkles,
    name: "Exterior Detail",
    tagline: "Showroom-level shine, outside and around.",
    includes: [
      "Hand wash",
      "Wheel & tire clean",
      "Tire shine",
      "Exterior windows",
      "Spray wax / sealant",
    ],
    pricing: { coupe: 80, sedan: 90, small_suv: 100, large_suv: 110, truck: 120 },
  },
  {
    id: "interior",
    icon: Car,
    name: "Interior Detail",
    tagline: "Cabin reset, every seat, vent, and crevice.",
    includes: [
      "Full vacuum",
      "Interior wipe down",
      "Plastics cleaned",
      "Interior windows",
      "Mats cleaned",
      "Steam cleaning service",
      "Light stain removal",
    ],
    pricing: { coupe: 110, sedan: 125, small_suv: 140, large_suv: 155, truck: 160 },
  },
  {
    id: "full",
    icon: Wrench,
    name: "Full Detail",
    tagline: "Best value, inside and outside, top to bottom.",
    includes: [
      "Everything in Exterior Detail",
      "Everything in Interior Detail",
      "Combined service · best value",
    ],
    pricing: { coupe: 170, sedan: 180, small_suv: 200, large_suv: 215, truck: 230 },
    featured: true,
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-slate-50 border-y border-slate-200"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7">
            <div className="text-[11px] tracking-[0.3em] uppercase font-bold text-blue-600 mb-4">
              Services & Pricing
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-slate-900">
              Honest pricing.<br />
              Priced by vehicle size.
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-slate-600 text-base leading-relaxed">
              Pick your service, find your vehicle size, and you'll know
              exactly what it costs. No surprises, no upsells, just clean
              cars and fair work.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 mb-16">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="relative bg-white p-8 md:p-10 flex flex-col group hover:bg-slate-900 transition-colors duration-300"
              >
                <div className="flex items-start justify-between mb-8">
                  <div className="h-12 w-12 grid place-items-center border-2 border-slate-900 group-hover:border-blue-400 transition-colors">
                    <Icon size={22} strokeWidth={2} className="text-slate-900 group-hover:text-blue-400 transition-colors" />
                  </div>
                  {s.featured && (
                    <span className="text-[10px] tracking-[0.25em] uppercase font-bold bg-blue-600 text-white px-2 py-1">
                      Best Value
                    </span>
                  )}
                </div>

                <h3 className="font-display font-black text-3xl md:text-4xl text-slate-900 group-hover:text-white transition-colors tracking-tight">
                  {s.name}
                </h3>
                <p className="mt-3 text-sm text-slate-600 group-hover:text-slate-300 transition-colors">
                  {s.tagline}
                </p>

                <div className="mt-7 border border-slate-200 group-hover:border-slate-700 transition-colors" data-testid={`service-pricing-${s.id}`}>
                  {VEHICLE_SIZES.map((v, idx) => (
                    <div
                      key={v.key}
                      className={`flex items-center justify-between px-4 py-3 text-sm ${
                        idx !== VEHICLE_SIZES.length - 1 ? "border-b border-slate-200 group-hover:border-slate-700" : ""
                      }`}
                    >
                      <span className="text-slate-700 group-hover:text-slate-200 transition-colors">
                        {v.label}
                      </span>
                      <span className="font-display font-black text-lg text-slate-900 group-hover:text-blue-400 transition-colors">
                        ${s.pricing[v.key]}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-6">
                  <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-slate-500 group-hover:text-blue-400 mb-3 transition-colors">
                    Includes
                  </div>
                  <ul className="space-y-2">
                    {s.includes.map((i) => (
                      <li key={i} className="text-xs flex items-start gap-2 text-slate-700 group-hover:text-slate-200 transition-colors">
                        <Check size={14} strokeWidth={2.5} className="text-blue-600 group-hover:text-blue-400 mt-0.5 flex-shrink-0 transition-colors" />
                        {i}
                      </li>
                    ))}
                  </ul>
                </div>

                <a
                  href="#book"
                  data-testid={`service-book-${s.id}`}
                  className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-slate-900 group-hover:text-white transition-colors"
                >
                  Book this service
                  <ArrowUpRight size={16} strokeWidth={2.5} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              </div>
            );
          })}
        </div>

        <div className="border border-slate-200 bg-white p-6 md:p-8 text-sm text-slate-600 flex flex-col md:flex-row gap-3 md:gap-6 md:items-center md:justify-between">
          <span>
            <strong className="text-slate-900 font-display font-bold">Not sure which size?</strong>{" "}
            Tell us your vehicle when you book and we'll confirm the exact price.
          </span>
          <a href="tel:5416222960" className="font-bold text-blue-600 hover:text-blue-700 whitespace-nowrap">
            Call 541-622-2960 →
          </a>
        </div>
      </div>
    </section>
  );
}
